import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { selectIsAuthenticated } from './store/authSlice';
import { signOut } from './store/authSlice';
import { selectCurrentGame } from './store/gameSlice';
import LoginPage from './pages/auth/LoginPage';
import SignUpPage from './pages/auth/SignUpPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import { LevelSelectionPage } from './pages/lobby/LevelSelectionPage';
import { GameSetupPage } from './pages/lobby/GameSetupPage';
import { AvatarSelectionPage } from './pages/lobby/AvatarSelectionPage';
import GamePage from './pages/game/GamePage';
import AuctionPage from './pages/auction/AuctionPage';
import { DebugPanel } from './components/Debug/DebugPanel';
import { stopMusic } from './audio/rushAudio';
import './App.css';

function App() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const game = useSelector(selectCurrentGame);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // The pulse lives outside any page. Stop it everywhere except the table and the auction.
  useEffect(() => {
    const path = location.pathname;
    const stillPlaying = path.startsWith('/game/') || path.startsWith('/auction/');
    if (!stillPlaying) stopMusic();
  }, [location.pathname]);

  // Global handler for session expiration
  useEffect(() => {
    const handleSessionExpired = (event: CustomEvent) => {
      // Clear auth state
      dispatch(signOut() as any);
      
      // Navigate to login with message
      const message = event.detail?.message || 'Your session has expired. Please sign in again.';
      navigate('/login', { 
        state: { 
          sessionExpired: true,
          message: message 
        },
        replace: true 
      });
    };

    // Listen for session expiration events from API client
    window.addEventListener('auth:session-expired', handleSessionExpired as EventListener);

    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired as EventListener);
    };
  }, [dispatch, navigate]);

  return (
    <div className="app">
      <DebugPanel currentPhase={game?.phase} gameId={game?.gameId} />
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/lobby" replace /> : <LoginPage />}
        />
        <Route
          path="/signup"
          element={isAuthenticated ? <Navigate to="/lobby" replace /> : <SignUpPage />}
        />
        <Route
          path="/verify-email"
          element={isAuthenticated ? <Navigate to="/lobby" replace /> : <VerifyEmailPage />}
        />
        <Route
          path="/lobby"
          element={isAuthenticated ? <LevelSelectionPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/lobby/setup/:levelId"
          element={isAuthenticated ? <GameSetupPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/lobby/avatars/:gameId"
          element={isAuthenticated ? <AvatarSelectionPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/game/:gameId"
          element={isAuthenticated ? <GamePage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/auction/:gameId"
          element={isAuthenticated ? <AuctionPage /> : <Navigate to="/login" replace />}
        />
        <Route path="/" element={<Navigate to={isAuthenticated ? '/lobby' : '/login'} replace />} />
      </Routes>
    </div>
  );
}

export default App;

